/**
 * FP&A: finalidade do frete e identificação dos gastos de aquisição (ADR 0032, seção 13).
 *
 * Frete não é, por si, distribuição do que foi vendido nem despesa comercial: ele acompanha a
 * operação que serve (divulgação, conteúdo do produto, desenvolvimento, compra de material, entrega
 * ao cliente ou envio administrativo). A finalidade é registrada no lançamento e diz qual Tipo de
 * Gasto o frete deve seguir.
 *
 * Gastos de aquisição (CAC) são uma marca transversal ao plano de classificação: o lançamento
 * participa, não participa ou ainda não foi classificado, com o percentual quando o gasto é
 * compartilhado e o critério que sustenta a inclusão. Nada é marcado sozinho.
 */
import { z } from 'zod';
import { toScaled } from './money.ts';

export const FPA_FREIGHT_PURPOSES = [
  'promotional',
  'product_content',
  'development',
  'acquisition',
  'customer_delivery',
  'administrative',
] as const;
export type FpaFreightPurpose = (typeof FPA_FREIGHT_PURPOSES)[number];

/**
 * Tipo de Gasto que o frete acompanha, pela finalidade (nomes do catálogo v3.1, com os nomes
 * antigos e os propostos aceitos). A conferência só vale quando o cadastro tem algum desses nomes.
 */
export const FREIGHT_EXPENSE_TYPES: Record<FpaFreightPurpose, readonly string[]> = {
  // Envio de miniaturas a pintores para vídeos e divulgação: a despesa de marketing.
  promotional: ['Despesas Comerciais'],
  // Conteúdo que integra o produto ou a assinatura: custo do que é vendido.
  product_content: ['Custos Operacionais', 'Custos dos Produtos e Serviços'],
  // Protótipos e testes de produto novo.
  development: ['Desenvolvimento de Produtos'],
  // Compra de material ou mercadoria: entra no custo do que foi comprado (estoque ou ativo).
  acquisition: ['Estoque', 'CAPEX'],
  // Entrega do que foi vendido ao cliente.
  customer_delivery: ['Despesas Comerciais'],
  administrative: ['Despesas Gerais e Administrativas'],
};

const norm = (s: string) =>
  s.normalize('NFD').replace(/\p{M}/gu, '').trim().toUpperCase().replace(/\s+/g, ' ');

/** A classificação é de frete (Correios e fretes, fretes, frete de…)? */
export function isFreightClassification(name: string | null | undefined): boolean {
  const n = norm(name ?? '');
  return /\bFRETES?\b/.test(n) || /\bCORREIOS?\b/.test(n);
}

/**
 * O Tipo de Gasto escolhido segue a finalidade do frete? `null` quando o cadastro não tem nenhum
 * dos nomes esperados (a conferência não se aplica e nada é inventado).
 */
export function freightTypeMatches(
  purpose: FpaFreightPurpose,
  expenseTypeName: string,
  catalogNames: readonly string[],
): boolean | null {
  const expected = new Set(FREIGHT_EXPENSE_TYPES[purpose].map(norm));
  if (!catalogNames.some((c) => expected.has(norm(c)))) return null;
  return expected.has(norm(expenseTypeName));
}

/**
 * Pergunta "Esse gasto afetará o CAC?" (decisão de 28/10/2026): included = Sim, excluded = Não,
 * unclassified = ainda sem resposta (o histórico sem resposta nunca vira Não). Obrigatória quando
 * algum centro de custo é de marketing (core.cost_center_is_marketing), como em Compras e no
 * Contas a Pagar.
 */
export const FPA_ACQUISITION_STATUSES = ['unclassified', 'included', 'excluded'] as const;
export type FpaAcquisitionStatus = (typeof FPA_ACQUISITION_STATUSES)[number];

const percent = z
  .string()
  .trim()
  .regex(/^\d{1,3}(\.\d{1,4})?$/, 'Use um percentual com até 4 casas');

/** Campos de finalidade e aquisição, iguais na despesa planejada e no ajuste do Realizado. */
export const fpaPurposeFields = {
  freightPurpose: z
    .enum(FPA_FREIGHT_PURPOSES)
    .nullish()
    .transform((v) => v ?? null),
  acquisitionStatus: z.enum(FPA_ACQUISITION_STATUSES).default('unclassified'),
  /** Parte do gasto que é aquisição (gasto compartilhado); vazio = 100%. */
  acquisitionPercent: percent.nullish().transform((v) => v ?? null),
  /** Critério da resposta, opcional (ex.: "mídia paga de captação de novos assinantes"). */
  acquisitionBasis: z
    .string()
    .trim()
    .max(500)
    .nullish()
    .transform((v) => v || null),
};

/** Conferências comuns: percentual só para quem afeta o CAC. */
export function checkAcquisition(
  v: {
    acquisitionStatus: FpaAcquisitionStatus;
    acquisitionPercent: string | null;
    acquisitionBasis: string | null;
  },
  issue: (path: string, message: string) => void,
) {
  if (v.acquisitionStatus !== 'included') {
    if (v.acquisitionPercent) issue('acquisitionPercent', 'Percentual só para quem participa');
    return;
  }
  if (v.acquisitionBasis && v.acquisitionBasis.length < 3) {
    issue('acquisitionBasis', 'Use pelo menos 3 caracteres no critério');
  }
  if (v.acquisitionPercent) {
    const p = toScaled(v.acquisitionPercent);
    if (p <= 0n || p > toScaled('100'))
      issue('acquisitionPercent', 'Use um percentual entre 0 e 100');
  }
}

/** Parte de aquisição de um valor (percentual vazio = 100%), com 4 casas. */
export function acquisitionShare(amount: string, percentValue: string | null): bigint {
  const total = toScaled(amount);
  if (!percentValue) return total;
  const num = total * toScaled(percentValue);
  const scale = 1_000_000n;
  const q = num / scale;
  const r = num - q * scale;
  // Metade para longe do zero.
  return 2n * (r < 0n ? -r : r) >= scale ? q + (num < 0n ? -1n : 1n) : q;
}

/** Filtro "Afeta o CAC": Sim, Não, ainda sem resposta (centros de marketing) ou todos. */
export const FPA_CAC_FILTERS = ['yes', 'no', 'unanswered', 'all'] as const;
export type FpaCacFilter = (typeof FPA_CAC_FILTERS)[number];

export const fpaAcquisitionQuery = z.object({
  versionId: z.uuid(),
  cac: z.enum(FPA_CAC_FILTERS).default('yes'),
  legalEntityId: z.uuid().optional(),
  from: z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
    .optional(),
  until: z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
    .optional(),
});
